/**
 * PWA Install — service worker registration + install prompt logic.
 *
 * Handles:
 * - Registering /sw.js so the app is installable and has offline fallback
 * - Android/Chrome: intercepts `beforeinstallprompt` and shows a custom banner
 * - iOS/Safari: detects the platform and shows manual install instructions
 * - Already installed (standalone mode): skips banner entirely
 *
 * Follows the same module pattern as version-guard.ts — loaded once from
 * base.html, no page-specific wiring required.
 */

import { logger } from './logger.js';

const DISMISSED_KEY = 'pwaInstallDismissed';
const BANNER_ID = 'pwaBanner';

// ─── BeforeInstallPromptEvent type (not yet in TS lib) ───────────────────────

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

// ─── Service Worker Registration ─────────────────────────────────────────────

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (reg) => logger.info(`[PWA] SW registered, scope: ${reg.scope}`),
      (err) => logger.info(`[PWA] SW registration failed: ${err}`),
    );
  });
}

// ─── Install prompt (Android / Chrome) ───────────────────────────────────────

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  || (navigator as Navigator & { standalone?: boolean }).standalone === true;

if (!isStandalone && !localStorage.getItem(DISMISSED_KEY)) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // Suppress the default mini-infobar
    deferredPrompt = e;
    showBanner('android');
  });

  window.addEventListener('appinstalled', () => {
    logger.info('[PWA] App installed');
    hideBanner();
    deferredPrompt = null;
  });

  // iOS Safari: no beforeinstallprompt — show manual instructions
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isInSafari = /safari/i.test(navigator.userAgent) && !/crios|fxios|opios/i.test(navigator.userAgent);
  if (isIos && isInSafari) {
    showBanner('ios');
  }
}

// ─── Banner UI ────────────────────────────────────────────────────────────────

function buildBanner(platform: 'android' | 'ios'): HTMLElement {
  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.className = 'pwa-banner';
  banner.setAttribute('role', 'complementary');
  banner.setAttribute('aria-label', 'Install Fortunio');

  const iosBody = `Tap <span class="pwa-banner__share-icon bi bi-box-arrow-up" aria-label="share"></span> then &ldquo;Add to Home Screen&rdquo;`;
  const body = platform === 'ios' ? iosBody : '';
  const ctaButton = platform === 'android'
    ? `<button class="pwa-banner__cta" id="pwaInstallBtn" type="button">Add to Home Screen</button>`
    : '';

  banner.innerHTML = `
    <span class="pwa-banner__icon" aria-hidden="true">🎲</span>
    <div class="pwa-banner__text">
      <p class="pwa-banner__title">
        ${platform === 'android' ? 'Play Fortunio from your home screen' : 'Add Fortunio to your home screen'}
      </p>
      ${body ? `<p class="pwa-banner__body">${body}</p>` : ''}
    </div>
    <div class="pwa-banner__actions">
      ${ctaButton}
      <button class="pwa-banner__dismiss" id="pwaBannerDismiss" type="button" aria-label="Dismiss">
        <span class="bi bi-x-lg" aria-hidden="true"></span>
      </button>
    </div>
  `;

  return banner;
}

function showBanner(platform: 'android' | 'ios'): void {
  if (document.getElementById(BANNER_ID)) return; // Already shown

  const banner = buildBanner(platform);
  document.body.appendChild(banner);
  document.body.classList.add('pwa-banner-visible');

  // Dismiss button
  document.getElementById('pwaBannerDismiss')?.addEventListener('click', () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    hideBanner();
  });

  // Android install button
  if (platform === 'android') {
    document.getElementById('pwaInstallBtn')?.addEventListener('click', async () => {
      if (!deferredPrompt) return;
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      logger.info(`[PWA] Install prompt outcome: ${outcome}`);
      deferredPrompt = null;
      hideBanner();
    });
  }

  logger.info(`[PWA] Install banner shown (${platform})`);
}

function hideBanner(): void {
  const banner = document.getElementById(BANNER_ID);
  if (banner) banner.remove();
  document.body.classList.remove('pwa-banner-visible');
}
